import { BRAND } from "../lib/brand";
import { Instagram, Facebook, Mail, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useContent } from "../context/ContentContext";

export default function Footer() {
    const { content } = useContent();
    const f = content.footer;
    const logo = content.brand?.logo || BRAND.logo;

    const waNumber = (f.whatsapp_number || "").replace(/[^\d]/g, "");
    const waLink = waNumber
        ? `https://wa.me/${waNumber}?text=${encodeURIComponent(f.whatsapp_message || "")}`
        : null;

    return (
        <footer data-testid="footer" className="bg-black border-t border-meraki-gold/20 pt-20 pb-10">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-4">
                            <img src={logo} alt="Meraki Riders" className="w-20 h-20 object-contain" />
                            <div>
                                <div className="font-display text-3xl text-meraki-gold leading-none">MERAKI</div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-meraki-muted mt-1">Riders Group · Est. 2021</div>
                            </div>
                        </div>
                        <p className="mt-6 text-meraki-muted text-sm leading-relaxed max-w-md whitespace-pre-line">{f.tagline}</p>
                        {f.contact_email && (
                            <a
                                href={`mailto:${f.contact_email}?subject=Collaboration with Meraki Riders Group`}
                                data-testid="footer-email-link"
                                className="mt-6 inline-flex items-center gap-3 px-4 py-3 border border-meraki-gold/40 hover:border-meraki-gold hover:bg-meraki-gold/5 transition-colors group"
                            >
                                <Mail className="w-4 h-4 text-meraki-gold" />
                                <div className="flex flex-col items-start leading-tight">
                                    {f.contact_label && (
                                        <span className="text-[9px] uppercase tracking-[0.3em] text-meraki-muted">{f.contact_label}</span>
                                    )}
                                    <span className="text-sm text-meraki-text group-hover:text-meraki-gold transition-colors">{f.contact_email}</span>
                                </div>
                            </a>
                        )}
                        {waLink && (
                            <a
                                href={waLink}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-whatsapp-link"
                                className="mt-3 ml-0 sm:ml-3 inline-flex items-center gap-3 px-4 py-3 border border-meraki-gold/40 hover:border-meraki-gold hover:bg-meraki-gold/5 transition-colors group"
                            >
                                <MessageCircle className="w-4 h-4 text-meraki-gold" />
                                <div className="flex flex-col items-start leading-tight">
                                    <span className="text-[9px] uppercase tracking-[0.3em] text-meraki-muted">{f.whatsapp_label || "Chat on WhatsApp"}</span>
                                    <span className="text-sm text-meraki-text group-hover:text-meraki-gold transition-colors">+{waNumber}</span>
                                </div>
                            </a>
                        )}
                    </div>

                    <div className="md:col-span-3">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-meraki-gold mb-5">Explore</div>
                        <ul className="space-y-3 text-sm">
                            <li><a href="#about" className="text-meraki-text/80 hover:text-meraki-gold transition-colors">About</a></li>
                            <li><a href="#how" className="text-meraki-text/80 hover:text-meraki-gold transition-colors">How We Work</a></li>
                            <li><a href="#leaders" className="text-meraki-text/80 hover:text-meraki-gold transition-colors">Leaders</a></li>
                            <li><a href="#stats" className="text-meraki-text/80 hover:text-meraki-gold transition-colors">Dashboard</a></li>
                            <li><Link to="/admin/login" className="text-meraki-muted hover:text-meraki-gold transition-colors">Admin</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-meraki-gold mb-5">Find Us</div>
                        <div className="space-y-3">
                            <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-meraki-text/80 hover:text-meraki-gold transition-colors text-sm">
                                <Instagram className="w-4 h-4" />
                                <span>instagram.com/merakiriders</span>
                            </a>
                            <a href={BRAND.facebook} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-meraki-text/80 hover:text-meraki-gold transition-colors text-sm">
                                <Facebook className="w-4 h-4" />
                                <span>Meraki Riders Group</span>
                            </a>
                            <a href={BRAND.webapp} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-meraki-text/80 hover:text-meraki-gold transition-colors text-sm">
                                <span className="w-4 h-4 inline-block border border-current" />
                                <span>merakiriders.in</span>
                            </a>
                            {f.contact_email && (
                                <a
                                    href={`mailto:${f.contact_email}?subject=Collaboration with Meraki Riders Group`}
                                    data-testid="footer-email-link-sidebar"
                                    className="flex items-center gap-3 text-meraki-text/80 hover:text-meraki-gold transition-colors text-sm"
                                >
                                    <Mail className="w-4 h-4" />
                                    <span>{f.contact_email}</span>
                                </a>
                            )}
                            {waLink && (
                                <a
                                    href={waLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="footer-whatsapp-link-sidebar"
                                    className="flex items-center gap-3 text-meraki-text/80 hover:text-meraki-gold transition-colors text-sm"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    <span>WhatsApp +{waNumber}</span>
                                </a>
                            )}
                        </div>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {BRAND.hashtags.map((t) => (
                                <span key={t} className="text-xs font-mono text-meraki-gold/70">{t}</span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="pt-8 border-t border-meraki-gold/15 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div data-testid="footer-copyright" className="text-[10px] uppercase tracking-[0.3em] text-meraki-muted">
                        {f.copyright}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-meraki-muted">
                        {f.rightside}
                    </div>
                </div>
            </div>
        </footer>
    );
}
